import { useMemo } from 'react';

import type { AggregatedIssue, Issue } from '@/entities/issue';
import type { Signal } from '@/entities/signal';
import { calculatePeriodCounts } from '@/features/(signal)/signals/lib';
import type {
  CompositedSignalIssue,
  SIGNAL_SEVERITY,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';

interface UseComputedIssueParams {
  aggregatedIssue?: AggregatedIssue;
  originIssues: Issue[];
  issueName: string;
  datePreset: SignalDateSelectionInput;
  signals: Signal[];
}

/**
 * 단일 issue의 CompositedSignalIssue를 계산하는 훅
 * Detail page에서 사용 (리스트가 아닌 단일 issue 처리용)
 */
export const useComputedIssue = ({
  aggregatedIssue,
  originIssues,
  issueName,
  datePreset,
  signals,
}: UseComputedIssueParams): CompositedSignalIssue | null => {
  return useMemo(() => {
    // signal id → name 매핑
    const signalMap = new Map(signals.map((s) => [s.id, s.title]));

    const issueChunk = aggregatedIssue ?? {
      name: issueName,
      values: [],
    };

    const originalIssue = originIssues.find((i) => i.name === issueName);
    if (!originalIssue) return null;
    if (originalIssue.hidden) return null;

    const { selectedPeriodCount, comparisonPeriodCount } =
      calculatePeriodCounts(issueChunk.values, datePreset);

    // signal_ids를 signal 이름으로 변환
    const issueSignalIds = originalIssue.signal_ids ?? [];
    const signalTitles = issueSignalIds
      .map((id) => signalMap.get(id))
      .filter((name): name is string => name != null);

    return {
      issueIndex: 1,
      name: issueChunk.name,
      severity: originalIssue.severity as SIGNAL_SEVERITY,
      favorite: originalIssue.favorite,
      reason: originalIssue.reason,
      detectedAt: originalIssue.detected_at,
      issueDateChunks: issueChunk.values,
      selectedPeriodCount,
      comparisonPeriodCount,
      signalIds: issueSignalIds,
      signalTitles,
    };
  }, [aggregatedIssue, issueName, datePreset, originIssues, signals]);
};
